import { useState } from 'react';

import { sanitizeBlockHtml } from '@/lib/sanitize';
import type { DocumentEntity } from '@/context/store/slices/documentDataSlice';

import { ImageLightbox } from './image-lightbox';

import './document-view-image.scss';

type Props = { node: DocumentEntity & { type: 'image' } };

function DocumentViewImage({ node }: Props) {
  const [open, setOpen] = useState(false);
  const url = node.assetUrl ?? node.src;
  const alignment = node.alignment ?? 'center';

  return (
    <div
      id={node.id}
      data-type="card"
      className={`doc-view-image doc-view-image--${alignment}`}
    >
      {url ? (
        <button
          type="button"
          className="doc-view-image__zoom"
          onClick={() => setOpen(true)}
          aria-label="Zoom image"
        >
          <img
            src={url}
            alt={node.alt ?? ''}
            width={node.width}
            height={node.height}
            className="doc-view-image__img"
          />
        </button>
      ) : (
        <div
          className="doc-view-image__empty"
          role="img"
          aria-label={node.alt ?? 'Image not yet uploaded'}
        >
          <div className="doc-view-image__empty-title">
            Image not yet uploaded
          </div>
          {node.alt && (
            <div className="doc-view-image__empty-alt">{node.alt}</div>
          )}
          <div className="doc-view-image__empty-hint">
            Open this document in the editor to upload the image.
          </div>
        </div>
      )}
      {node.caption && (
        <div
          className="doc-view-image__caption"
          dangerouslySetInnerHTML={{ __html: sanitizeBlockHtml(node.caption) }}
        />
      )}
      {url && (
        <ImageLightbox
          isOpen={open}
          url={url}
          alt={node.alt ?? ''}
          caption={node.caption}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export { DocumentViewImage };
